import React, { useState } from 'react';
import { fmt } from '../utils/api';
import useCollapsible from '../utils/useCollapsible';

const WEEKS_PER_MONTH = 4.33;

export default function BillsVsSpendable({ bills, budget }) {
  const [collapsed, toggleCollapsed] = useCollapsible('billsVsSpendable');
  const [showItems, setShowItems] = useState(false);

  if (!bills || !budget) return null;

  const billsTotal = bills.total || 0;
  const monthBudget = budget.month?.budget || 0;
  const weekBudget = budget.week?.budget || 0;
  const weeklyBillsShare = billsTotal / WEEKS_PER_MONTH;

  const spendableMonth = monthBudget - billsTotal;
  const spendableWeek = weekBudget - weeklyBillsShare;

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <button style={styles.collapseBtn} onClick={toggleCollapsed}>
          <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
          <h2 style={styles.title}>🧾 Bills vs. Spendable</h2>
        </button>
      </div>

      {!collapsed && (
        <>
          <p style={styles.subtext}>
            What's already spoken for in fixed monthly bills, versus what's actually free to spend on everything else.
          </p>

          <div style={styles.mathRow}>
            <div style={styles.mathCol}>
              <span style={styles.mathLabel}>Monthly budget</span>
              <span style={styles.mathValue}>{fmt.currency(monthBudget)}</span>
            </div>
            <span style={styles.mathOp}>&minus;</span>
            <div style={styles.mathCol}>
              <span style={styles.mathLabel}>Est. monthly bills</span>
              <span style={{ ...styles.mathValue, color: '#f97316' }}>{fmt.currency(billsTotal)}</span>
            </div>
            <span style={styles.mathOp}>=</span>
            <div style={styles.mathCol}>
              <span style={styles.mathLabel}>Spendable this month</span>
              <span style={{ ...styles.mathValue, color: spendableMonth >= 0 ? '#00d4aa' : '#ef4444', fontSize: '1.1rem' }}>
                {fmt.currency(spendableMonth)}
              </span>
            </div>
          </div>

          <div style={styles.weekRow}>
            <span style={styles.weekLabel}>This week: {fmt.currency(weekBudget)} budget &minus; ~{fmt.currency(weeklyBillsShare)} bills share</span>
            <span style={{ ...styles.weekValue, color: spendableWeek >= 0 ? '#00d4aa' : '#ef4444' }}>
              = {fmt.currency(spendableWeek)} spendable
            </span>
          </div>

          {bills.items && bills.items.length > 0 && (
            <div style={styles.itemsSection}>
              <button style={styles.itemsToggle} onClick={() => setShowItems((s) => !s)}>
                {showItems ? 'Hide' : 'Show'} the {bills.items.length} bill{bills.items.length === 1 ? '' : 's'} making up {fmt.currency(billsTotal)}/mo {showItems ? '▾' : '▸'}
              </button>
              {showItems && (
                <div style={styles.itemsList}>
                  {bills.items.map((item) => (
                    <div key={item.name} style={styles.itemRow}>
                      <span style={styles.itemName}>{item.name}</span>
                      <span style={styles.itemFreq}>{item.frequency}</span>
                      <span style={styles.itemAmount}>{fmt.currency(item.monthlyAmount)}/mo</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.6rem',
  },
  title: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  collapseBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  chevron: {
    color: '#6b7280',
    fontSize: '0.8rem',
    width: '0.8rem',
  },
  subtext: {
    color: '#6b7280',
    fontSize: '0.78rem',
    margin: '0 0 1.1rem 0',
    lineHeight: 1.5,
  },
  mathRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    marginBottom: '0.9rem',
  },
  mathCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  mathLabel: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'Syne', sans-serif",
  },
  mathValue: {
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  mathOp: {
    color: '#4b5563',
    fontSize: '1.2rem',
    fontWeight: '300',
  },
  weekRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.4rem',
    padding: '0.7rem 0.85rem',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '8px',
    marginBottom: '0.6rem',
  },
  weekLabel: { color: '#9ca3af', fontSize: '0.75rem' },
  weekValue: { fontSize: '0.8rem', fontWeight: '700', fontFamily: "'DM Mono', monospace" },
  itemsSection: { marginTop: '0.6rem' },
  itemsToggle: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.76rem',
    fontWeight: '600',
    cursor: 'pointer',
    padding: 0,
    fontFamily: "'Syne', sans-serif",
  },
  itemsList: { marginTop: '0.6rem' },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.35rem 0',
    borderBottom: '1px solid #161f2c',
    fontSize: '0.78rem',
  },
  itemName: { color: '#d1d5db', flex: 1 },
  itemFreq: { color: '#6b7280', textTransform: 'capitalize', fontSize: '0.7rem' },
  itemAmount: { color: '#fff', fontWeight: '600', fontFamily: "'DM Mono', monospace" },
};
